/**
 * Configuration for rendering different states of nodes in the graph.
 */

/**
 * Color schema: The color is different for each type of node.
 * The highlight color is used when the node is hovered or labeled.
 * The highlight color is brighter and more saturated than the normal color.
 */
const schema = {
    Study: { color: '#9b6b43', highlight: '#684328' },
    Cohort: { color: '#374151', highlight: '#111827' },
    'Study / Cohort': { color: '#374151', highlight: '#111827' },
    'Biomedical Entities': { color: '#e16b23', highlight: '#a34410' },
    'Attributes & Measurements': { color: '#2979b8', highlight: '#17517f' },
    'Methods & Study Design': { color: '#7957b8', highlight: '#513381' },
    Context: { color: '#38865a', highlight: '#205737' },
    GEO: {
        color: '#88d667',
        highlight: '#4fb224',
    },
    PERSON: {
        color: '#e0b377',
        highlight: '#f78d00',        
    },
    ORGANIZATION: {
        color: '#71abde',
        highlight: '#1d63a2',
    },
    EVENT: {
        color: '#d9adc4',
        highlight: '#e48cba',
    },
    DEFAULT: {
        color: 'grey',
        highlight: 'grey',
    }
}

// Context-only palette: schemeObservable10 in its canonical order.
// Shared by the Context-only canvas, legend, and hover/selection highlights.
const contextSubtypeSchema = {
    People: { color: '#4269d0', highlight: '#2e4992' },
    Organizations: { color: '#efb118', highlight: '#a77c11' },
    Locations: { color: '#ff725c', highlight: '#b35040' },
    Events: { color: '#6cc5b0', highlight: '#4c8a7b' },
    Other: { color: '#9498a0', highlight: '#686a70' },
};

function contextSubtype(node) {
    return Object.hasOwn(contextSubtypeSchema, node.context_subtype) ? node.context_subtype : 'Other';
}

function nodeColors(node, colorByContextSubtype) {
    return colorByContextSubtype && (node.category_tags || [node.type]).includes('Context')
        ? contextSubtypeSchema[contextSubtype(node)]
        : schema[node.type] || schema.DEFAULT;
}

/**
 * Get the highlight color for a node based on its type.
 * @param {*} node - The node object to render.
 * @returns {string} - The highlight color for the node.
 */
function highlightNode(node, colorByContextSubtype = false) {
    return nodeColors(node, colorByContextSubtype).highlight;
}

/**
 * Get the color for a node based on its type.
 * @param {*} node - The node object to render.
 * @returns {string} - The color for the node.
 */
function colorNode(node, colorByContextSubtype = false) {
    return nodeColors(node, colorByContextSubtype).color;
}

/** * Function to render a label for a selected node.
 * @param {*} node - The node object to render.
 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
 * @returns {void} - Draws the label on the canvas.
 */
function LabelSelectedNode(node, ctx, colorByContextSubtype = false) {
    const label = node.title || node.id || "Untitled Node";
    const fontSize = 5;
    const padding = 4;
    ctx.font = `${fontSize}px Arial`;
    const textWidth = ctx.measureText(label).width;
    const w = textWidth + padding * 2;
    const h = fontSize + padding * 2;
    ctx.font = `${fontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = highlightNode(node, colorByContextSubtype);
    ctx.fillRect(node.x - w/2, node.y - h/2, w, h); // Draw background rectangle
    ctx.fillStyle = 'white';
    ctx.fillText(label, node.x, node.y); // Adjust y position for label
}

function LabelV2Node(node, ctx, globalScale, radius, colorByContextSubtype = false) {
    const scale = Math.max(globalScale || 1, 0.01);
    const fontSize = 13 / scale;
    const padding = 7 / scale;
    const lineHeight = 17 / scale;
    const maxWidth = 210 / scale;
    ctx.save();
    ctx.font = `500 ${fontSize}px Arial, sans-serif`;
    const words = String(node.title || node.name || node.id).split(/\s+/);
    const lines = [''];
    for (const word of words) {
        const index = lines.length - 1;
        const next = lines[index] ? `${lines[index]} ${word}` : word;
        if (ctx.measureText(next).width > maxWidth && lines[index] && lines.length < 2) lines.push(word);
        else lines[index] = next;
    }
    for (let i = 0; i < lines.length; i += 1) {
        if (ctx.measureText(lines[i]).width > maxWidth) {
            while (lines[i].length && ctx.measureText(`${lines[i]}…`).width > maxWidth) lines[i] = lines[i].slice(0, -1);
            lines[i] += '…';
        }
    }
    const width = Math.max(...lines.map(line => ctx.measureText(line).width)) + padding * 2;
    const height = lines.length * lineHeight + padding * 2;
    const x = node.x - width / 2;
    const y = node.y - radius - 8 / scale - height;
    const corner = 5 / scale;
    ctx.beginPath();
    ctx.arc(node.x, node.y, radius + 3 / scale, 0, Math.PI * 2);
    ctx.strokeStyle = highlightNode(node, colorByContextSubtype);
    ctx.lineWidth = 2 / scale;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x + corner, y);
    ctx.arcTo(x + width, y, x + width, y + height, corner);
    ctx.arcTo(x + width, y + height, x, y + height, corner);
    ctx.arcTo(x, y + height, x, y, corner);
    ctx.arcTo(x, y, x + width, y, corner);
    ctx.closePath();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
    ctx.fill();
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1 / scale;
    ctx.stroke();
    ctx.fillStyle = '#334155';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    lines.forEach((line, index) => ctx.fillText(line, node.x, y + padding + lineHeight * (index + 0.5)));
    ctx.restore();
}

/**
 * Function to render a legend for the node types.
 * @returns {JSX.Element} - A legend component that displays the color schema for different node types.
 */
function NodeLegend() {
    return (
        <div
            className="node-legend"
            style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '0.5rem',
                marginLeft: '50px',
                backgroundColor: 'transparent'
            }}
        >
            {Object.entries(schema).filter(([type]) => type !== 'Study / Cohort').map(([type, colors]) => (
                <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span
                        style={{
                            display: 'inline-block',
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: colors.color,
                            border: '1px solid #ccc',
                        }}
                    ></span>
                    <span className="node-label">{type}</span>
                </div>
            ))}
        </div>
    );
}

export {highlightNode, colorNode, LabelSelectedNode, LabelV2Node, NodeLegend, contextSubtype, contextSubtypeSchema};
